import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  limit 
} from 'firebase/firestore';
import { db } from '../firebase/config.js';

export const screenManagementService = {
  // Obtiene todas las pantallas del organizador
  async getOrganizerScreens(organizerId) {
    try {
      const screensRef = collection(db, 'screens');
      let snapshot;
      try {
        const q = query(
          screensRef,
          where('organizerId', '==', organizerId),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (err) {
        // Fallback sin índice compuesto
        const q = query(
          screensRef,
          where('organizerId', '==', organizerId)
        );
        snapshot = await getDocs(q);
      }
      const screens = [];
      
      snapshot.forEach(doc => {
        screens.push({
          id: doc.id,
          ...doc.data()
        });
      });
      // Ordenar en cliente si no se pudo usar orderBy
      screens.sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      return screens;
    } catch (error) {
      console.error('Error getting organizer screens:', error);
      throw error;
    }
  },

  // Obtiene pantallas vinculadas a un evento específico
  async getEventScreens(eventId) {
    try {
      const screensRef = collection(db, 'screens');
      let snapshot;
      try {
        const q = query(
          screensRef,
          where('eventId', '==', eventId),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (err) {
        const q = query(
          screensRef,
          where('eventId', '==', eventId)
        );
        snapshot = await getDocs(q);
      }
      const screens = [];
      
      snapshot.forEach(doc => {
        screens.push({
          id: doc.id,
          ...doc.data()
        });
      });
      screens.sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      
      return screens;
    } catch (error) {
      console.error('Error getting event screens:', error);
      throw error;
    }
  },

  // Obtiene pantallas disponibles (no vinculadas a eventos)
  async getAvailableScreens(organizerId) {
    try {
      const screensRef = collection(db, 'screens');
      let snapshot;
      try {
        const q = query(
          screensRef,
          where('organizerId', '==', organizerId),
          where('eventId', '==', null),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (err) {
        const q = query(
          screensRef,
          where('organizerId', '==', organizerId),
          where('eventId', '==', null)
        );
        snapshot = await getDocs(q);
      }
      const screens = [];
      
      snapshot.forEach(doc => {
        screens.push({
          id: doc.id,
          ...doc.data()
        });
      });
      screens.sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      return screens;
    } catch (error) {
      console.error('Error getting available screens:', error);
      throw error;
    }
  },

  // Crea una nueva pantalla
  async createScreen(screenData, organizerId) {
    try {
      const screenCode = await this.generateScreenCode();
      
      const newScreen = {
        ...screenData,
        organizerId,
        code: screenCode,
        status: 'inactive',
        eventId: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'screens'), newScreen);
      
      return {
        id: docRef.id,
        ...newScreen
      };
    } catch (error) {
      console.error('Error creating screen:', error);
      throw error;
    }
  },

  // Vincula una pantalla a un evento
  async linkScreenToEvent(screenId, eventId) {
    try {
      const screenRef = doc(db, 'screens', screenId);
      // Obtener joinCode del evento
      const evSnap = await getDoc(doc(db, 'events', eventId));
      const joinCode = evSnap.exists() ? (evSnap.data().joinCode || null) : null;

      // 1) Habilitar lectura pública de fotos para el evento primero (evita ventana de permisos en Screen)
      try {
        await updateDoc(doc(db, 'events', eventId), { allowPublicPhotos: true });
      } catch (_) {}

      // 2) Vincular la pantalla al evento
      await updateDoc(screenRef, {
        eventId,
        status: 'active',
        linkedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        joinCode,
        // limpiar cualquier orden de reinicio previa para evitar efectos residuales
        forceReset: null
      });
      
      return { success: true, screenId, eventId };
    } catch (error) {
      console.error('Error linking screen to event:', error);
      throw error;
    }
  },

  // Desvincula una pantalla de un evento
  async unlinkScreenFromEvent(screenId) {
    try {
      const screenRef = doc(db, 'screens', screenId);
      const snap = await getDoc(screenRef);
      const prevEventId = snap.exists() ? (snap.data().eventId || null) : null;
      await updateDoc(screenRef, {
        eventId: null,
        status: 'inactive',
        unlinkedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        joinCode: null
      });
      // Si no quedan pantallas vinculadas a ese evento, deshabilitar público
      if (prevEventId) {
        const qSnap = await getDocs(query(collection(db, 'screens'), where('eventId', '==', prevEventId), limit(1)));
        if (qSnap.empty) {
          try { await updateDoc(doc(db, 'events', prevEventId), { allowPublicPhotos: false }); } catch (_) {}
        }
      }
      
      return { success: true, screenId };
    } catch (error) {
      console.error('Error unlinking screen from event:', error);
      throw error;
    }
  },

  // Actualiza una pantalla
  async updateScreen(screenId, updateData) {
    try {
      const screenRef = doc(db, 'screens', screenId);
      await updateDoc(screenRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true, screenId };
    } catch (error) {
      console.error('Error updating screen:', error);
      throw error;
    }
  },

  // Elimina una pantalla
  async deleteScreen(screenId) {
    try {
      await deleteDoc(doc(db, 'screens', screenId));
      return { success: true, screenId };
    } catch (error) {
      console.error('Error deleting screen:', error);
      throw error;
    }
  },

  // Forzar reinicio en dispositivo (la Screen escuchará este campo)
  async forceResetScreen(screenId) {
    try {
      const screenRef = doc(db, 'screens', screenId);
      await updateDoc(screenRef, {
        // Marca orden de reinicio y deja explícito que ya NO está vinculada a una Screen
        forceReset: serverTimestamp(),
        clientLinked: false,
        clientLinkedAt: serverTimestamp(),
        isOnline: false,
        lastHeartbeat: null,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error forcing screen reset:', error);
      throw error;
    }
  },

  async getScreenById(screenId) {
    try {
      const ref = doc(db, 'screens', screenId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error('Error getting screen by id:', error);
      throw error;
    }
  },

  // Obtiene estadísticas de pantallas
  async getScreenStats(organizerId) {
    try {
      const screens = await this.getOrganizerScreens(organizerId);
      
      const stats = {
        total: screens.length,
        active: screens.filter(s => s.status === 'active').length,
        inactive: screens.filter(s => s.status === 'inactive').length,
        linked: screens.filter(s => s.eventId).length,
        unlinked: screens.filter(s => !s.eventId).length
      };
      
      return stats;
    } catch (error) {
      console.error('Error getting screen stats:', error);
      throw error;
    }
  },

  // Genera un código único para la pantalla
  async generateScreenCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code;
    let attempts = 0;
    const maxAttempts = 10;
    
    do {
      code = '';
      for (let i = 0; i < 6; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      attempts++;
    } while (!(await this.isScreenCodeUnique(code)) && attempts < maxAttempts);
    
    if (attempts >= maxAttempts) {
      throw new Error('No se pudo generar un código único para la pantalla');
    }
    
    return code;
  },

  // Verifica si un código de pantalla es único
  async isScreenCodeUnique(code) {
    try {
      const screensRef = collection(db, 'screens');
      const q = query(screensRef, where('code', '==', code));
      const snapshot = await getDocs(q);
      
      return snapshot.empty;
    } catch (error) {
      console.error('Error checking screen code uniqueness:', error);
      return false;
    }
  }
};
